import { useState, useEffect } from 'react';
import { supabase, adminSetUserEmail } from './supabase';
import { C, F, Card, Btn } from './tokens';

// Self-service Change Email card. Renders inside the Settings surface for
// any logged-in role. Routes through the admin-set-email edge function via
// adminSetUserEmail — the function's gate treats self-management as
// always-allowed (callerId === targetUserId), so users update their own
// email through the same atomic path admins use for others. The edge
// function updates auth.users AND the app-table mirror in one server-side
// hop; on any failure it throws, so there is no partial-success state.
//
// isImpersonating: when true, the form is hidden and a note explains why,
// mirroring ChangePasswordCard so the impersonator can't change someone
// else's login email by accident.
export default function ChangeEmailCard({ isImpersonating = false }) {
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");

  // Display the active session's email so the user can see which account
  // they're about to change.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const email = data?.user?.email || "";
      if (email) setAccountEmail(email);
    });
    return () => { cancelled = true; };
  }, []);

  if (isImpersonating) {
    return (
      <Card style={{padding:"16px 18px"}}>
        <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:6}}>Change Email</div>
        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.55}}>
          You're working inside another user's portal. Return to your own account to change your email.
        </div>
      </Card>
    );
  }

  // Shape check: at least one char before "@", a "." somewhere after "@",
  // and at least one char after that dot. Cheap, lets the server do real
  // validation. Same-as-current check prevents pointless writes.
  const trimmed = newEmail.trim();
  const at = trimmed.indexOf('@');
  const dot = trimmed.indexOf('.', at);
  const shapeOk = at > 0 && dot > at + 1 && dot < trimmed.length - 1;
  const differentOk = trimmed.toLowerCase() !== (accountEmail || '').trim().toLowerCase();
  const newTouched = trimmed.length > 0;
  const shapeError = newTouched && !shapeOk;
  const sameError = newTouched && shapeOk && !differentOk;
  const canSave = shapeOk && differentOk && !saving;

  function changeNewEmail(v) {
    setNewEmail(v);
    if (success) setSuccess(false);
    if (error) setError("");
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(""); setSuccess(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const authUserId = user?.id;
      if (!authUserId) throw new Error("Not signed in.");
      await adminSetUserEmail(authUserId, trimmed);
      // adminSetUserEmail throws on any failure (the edge function returns
      // ok:true only when BOTH auth.users and the mirror are updated).
      // Reaching here means the change fully succeeded.
      setAccountEmail(trimmed);
      setNewEmail("");
      setSuccess(true);
    } catch (e) {
      const msg = e?.message || String(e);
      if (/already|registered|duplicate|422/i.test(msg)) {
        setError("That email is already in use by another account.");
      } else {
        setError(msg || "Failed to update email. Please try again.");
      }
    }
    setSaving(false);
  }

  return (
    <Card style={{padding:"16px 18px"}}>
      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:4}}>Change Email</div>
      <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:accountEmail?2:14}}>Update the email you sign in with.</div>
      {accountEmail && (
        <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:14}}>Current: {accountEmail}</div>
      )}

      <div style={{marginBottom:12}}>
        <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase"}}>New email</label>
        <input
          type="email"
          value={newEmail}
          onChange={e=>changeNewEmail(e.target.value)}
          onKeyDown={e=>{ if (e.key === "Enter" && canSave) handleSave(); }}
          placeholder="you@example.com"
          autoComplete="email"
          style={{
            width:"100%",
            background:C.bgAlt,
            border:`1px solid ${C.border}`,
            borderRadius:8,
            padding:"11px 13px",
            color:C.charcoal,
            fontFamily:F.body,
            fontSize:13,
            boxSizing:"border-box",
          }}
        />
        {shapeError && (
          <div style={{fontFamily:F.body,fontSize:11,color:C.red,marginTop:5}}>Enter a valid email address.</div>
        )}
        {sameError && (
          <div style={{fontFamily:F.body,fontSize:11,color:C.red,marginTop:5}}>That's your current email.</div>
        )}
      </div>

      {error && (
        <div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"9px 13px",fontFamily:F.body,fontSize:12,color:C.red,marginBottom:12}}>
          {error}
        </div>
      )}
      {success && (
        <div style={{background:C.greenBg,border:`1px solid ${C.greenBd}`,borderRadius:7,padding:"9px 13px",fontFamily:F.body,fontSize:12,color:C.green,marginBottom:12}}>
          Email updated. Use it next time you sign in.
        </div>
      )}

      <Btn variant="primary" onClick={handleSave} loading={saving} disabled={!canSave}>
        Update Email
      </Btn>
    </Card>
  );
}
